import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { sendApiRequest } from '../utils';
import { toast } from 'react-toastify';

const MyDirectNetwork = () => {
  const [networkData, setNetworkData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchNetworkData();
  }, []);

  const fetchNetworkData = async () => {
    try {
      setIsLoading(true);
      const response = await sendApiRequest('api/network/my-direct-network', {}, 'GET');
      
      if (response.data.success) {
        setNetworkData(response.data.data);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to fetch network data');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return networkData;

    return [...networkData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [networkData, sortConfig]);

  const filteredData = sortedData.filter(member => 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.memberId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-xl p-6 space-y-6 border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-400">
              My Direct Network
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage and view your direct team members</p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Stats */}
            <div className="flex items-center space-x-4">
              <div className="flex flex-col items-center bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">
                <span className="text-sm text-gray-500">Total Members</span>
                <span className="text-lg font-bold text-blue-600">{networkData.length}</span>
              </div>
              <div className="flex flex-col items-center bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">
                <span className="text-sm text-gray-500">Active Members</span>
                <span className="text-lg font-bold text-green-600">
                  {networkData.filter(m => m.status === 'active').length}
                </span>
              </div>
              <div className="flex flex-col items-center bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">
                <span className="text-sm text-gray-500">Inactive Members</span>
                <span className="text-lg font-bold text-red-600">
                  {networkData.filter(m => m.status === 'inactive').length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name, ID, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-blue-400 
                       focus:ring-2 focus:ring-blue-100 transition-all duration-200"
          />
          <span className="absolute right-4 top-2.5 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-left">
                <th onClick={() => handleSort('memberId')} 
                    className="p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors duration-200">
                  Member ID {getSortIcon('memberId')}
                </th>
                <th onClick={() => handleSort('name')} 
                    className="p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors duration-200">
                  Name {getSortIcon('name')}
                </th>
                <th onClick={() => handleSort('city')} 
                    className="p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors duration-200">
                  City {getSortIcon('city')}
                </th>
                <th onClick={() => handleSort('regDate')} 
                    className="p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors duration-200">
                  Reg. Date {getSortIcon('regDate')}
                </th>
                <th onClick={() => handleSort('actDate')} 
                    className="p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors duration-200">
                  Act. Date {getSortIcon('actDate')}
                </th>
                <th onClick={() => handleSort('status')} 
                    className="p-4 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors duration-200">
                  Status {getSortIcon('status')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-8">
                    <div className="flex items-center justify-center space-x-2">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </motion.div>
                      <span className="text-gray-500">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    No members found
                  </td>
                </tr>
              ) : (
                filteredData.map((member) => (
                  <tr key={member.memberId} className="border-t border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                    <td className="p-4">
                      <span className="font-medium text-blue-600">{member.memberId}</span>
                    </td>
                    <td className="p-4 font-medium">{member.name}</td>
                    <td className="p-4 text-gray-600">{member.city}</td>
                    <td className="p-4 text-gray-600">{member.regDate}</td>
                    <td className="p-4 text-gray-600">{member.actDate || '-'}</td>
                    <td className="p-4">
                      <span 
                        className={`px-3 py-1 rounded-full text-xs font-medium ${member.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'}`}
                      >
                        {member.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyDirectNetwork;